import type { Item } from '@/domain/types';
import { ModelAnswer, Rubric } from './ModelAnswer';

/**
 * The reasoning behind a question, shown every time one is answered.
 *
 * The app cannot invent reasoning, so this renders the best the imported data
 * holds, in descending order of usefulness: the `note` the chat wrote, the
 * worked steps of a "work" question, the model answer of a written one, and at
 * the very least which option was right and why that matters. When nothing was
 * imported it says so plainly, rather than showing an empty box — that is his
 * cue to re-import the session with notes.
 */

export function hasReasoning(item: Item): boolean {
  if (item.note?.trim()) return true;
  if (item.type === 'work') return Boolean(item.steps?.length);
  if (item.type === 'free') return Boolean(item.parts?.length);
  if (item.type === 'short') return Boolean(item.sample?.trim());
  return false;
}

export function optionLetter(index: number): string {
  return String.fromCharCode(65 + index);
}

export function Explanation({ item, showModelAnswer = false }: { item: Item; showModelAnswer?: boolean }) {
  const note = item.note?.trim();
  const answerIndex = item.answerIndex;
  const correctOption =
    item.type === 'mc' && answerIndex !== undefined ? item.opts?.[answerIndex] : undefined;

  return (
    <div className="stack">
      {correctOption !== undefined ? (
        <div className="card stack" style={{ gap: 4 }}>
          <span className="flashcard__label">The answer</span>
          <div>
            <strong className="mono">{optionLetter(answerIndex!)}</strong> — {correctOption}
          </div>
        </div>
      ) : null}

      {showModelAnswer && item.type !== 'mc' ? <ModelAnswer item={item} /> : null}

      {note ? (
        <div className="banner banner--info">
          <strong>Why, and how to get there</strong>
          <div style={{ whiteSpace: 'pre-wrap' }}>{note}</div>
        </div>
      ) : null}

      {/* An mc question keeps its rubric here; the written types show theirs
          inside the model answer above. */}
      {item.type === 'mc' ? <Rubric points={item.rubric} /> : null}

      {!hasReasoning(item) ? (
        <p className="faint" style={{ margin: 0 }}>
          No reasoning was imported for this question. Re-import the session asking for a note on
          every question and it will appear here.
        </p>
      ) : null}
    </div>
  );
}
